import {Button, Dialog, DialogActions, DialogContent, Divider, Stack, TextField} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import {useEffect, useMemo, useState} from "react";
import { LocalDateTimeFormatISO } from "../../../Utils/DateFormatPatternUtils";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {LocalizationProvider, MobileDateTimePicker} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {CheckCircleOutlined} from "@mui/icons-material";

type Props = {
    open: boolean;
    onClose: () => void;
    onConfirm: (endDateTimeIsoOrNull: string | null) => void;
    mode: "single" | "bulk";
    // opcional: pra mostrar info ou usar no texto
    selectedCount: number;
    defaultEnd?: Dayjs | null; // se quiser sugerir um valor
    loading?: boolean;
};

export default function FinalizeUnavailabilityDialog({
    open,
    onClose,
    onConfirm,
    mode,
    selectedCount,
    defaultEnd = null,
    loading = false
}: Props) {

    dayjs.locale("pt-br");

    const [endAt, setEndAt] = useState<Dayjs | null>(defaultEnd);

    useEffect(() => {
        if (!open) return;
        setEndAt(defaultEnd ?? null);
    }, [open, defaultEnd]);

    const title = mode === "single"
        ? "Tem certeza que deseja finalizar essa indisponibilidade?"
        : "Tem certeza que deseja finalizar as indisponibilidades selecionadas?";

    const confirmLabel = mode === "single"
        ? "FINALIZAR INDISPONIBILIDADE"
        : "FINALIZAR INDISPONIBILIDADES";

    const helper = useMemo(() => {
        return (
            <>
                Essa ação <b>registrará o fim da indisponibilidade</b> no sistema no dia e horário atual.
                {" "}
                Se preferir, personalize o término da indisponibilidade no campo abaixo.
            </>
        );
    }, []);

    function handleConfirm() {
        // se não preencher: manda null pro backend usar now()
        const iso = endAt ? LocalDateTimeFormatISO(endAt.toISOString()) : null;
        onConfirm(iso);
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    overflow: "hidden"
                }
            }}
        >
            <DialogContent sx={{ pt: 2.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <CheckCircleOutlined sx={{ fontSize: 22 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                        {title}
                    </Typography>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
                    {helper}
                </Typography>

                <Box sx={{ maxWidth: 420 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                        <MobileDateTimePicker
                            label="Término"
                            value={endAt}
                            onChange={(v: any) => setEndAt(v)}
                            renderInput={(params) => (
                                <TextField {...params} fullWidth size="small" />
                            )}
                        />
                    </LocalizationProvider>

                    {/* opcional: pequena dica */}
                    {mode === "bulk" && (
                        <Typography sx={{ mt: 1, fontSize: 11, color: "text.secondary" }}>
                            Itens selecionados: {selectedCount}
                        </Typography>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button variant="text" onClick={onClose} disabled={loading}>
                    CANCELAR
                </Button>

                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={loading || (mode === "bulk" && selectedCount < 2)}
                    sx={{ px: 2.5 }}
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}